import { useState, useEffect, useCallback, useRef } from 'react'
import { DoorQuestQuestion, DoorQuestGameConfig } from '../types'
import { getGameQuestions, getGameQuestionConfig } from '../data/gameQuestions'

const PLAYER_Z_POSITION = -0.5
const COLLISION_BACK_OFFSET = 1.2
const COLLISION_FRONT_OFFSET = 1.8
const OBSTACLE_START_Z = -25

const debugLog = (...args: any[]) => {
  if (import.meta?.env?.DEV) {
    console.log('[SurvivalRunner]', ...args)
  }
}

/**
 * Helper function để phát âm thanh
 * Đường dẫn file âm thanh: public/sounds/
 * - correct.mp3: Âm thanh khi trả lời đúng
 * - game-over.mp3: Âm thanh khi game over (trả lời sai)
 */
const playSoundEffect = (type: 'correct' | 'game-over') => {
  try {
    const audio = new Audio()
    const soundPath = type === 'correct' 
      ? '/sounds/correct.mp3' 
      : '/sounds/game-over.mp3'
    
    audio.src = soundPath
    audio.volume = 0.7 // 70% volume
    audio.play().catch((error) => {
      console.log('Không thể phát âm thanh:', error)
      // Fallback: Tạo âm thanh bằng Web Audio API nếu file không tồn tại
      createFallbackSound(type)
    })
  } catch (error) {
    console.log('Lỗi phát âm thanh:', error)
    // Fallback: Tạo âm thanh bằng Web Audio API
    createFallbackSound(type)
  }
}

/**
 * Fallback: Tạo âm thanh bằng Web Audio API nếu file không tồn tại
 */
const createFallbackSound = (type: 'correct' | 'game-over') => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    
    const createTone = (frequency: number, duration: number, type: OscillatorType = 'sine') => {
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
      oscillator.type = type
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration)
      
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + duration)
    }
    
    if (type === 'correct') {
      // Âm thanh vui vẻ khi đúng
      createTone(523, 0.15) // C5
      setTimeout(() => createTone(659, 0.15), 100) // E5
      setTimeout(() => createTone(784, 0.2), 200) // G5
    } else {
      // Âm thanh buồn khi sai
      createTone(200, 0.4, 'sawtooth') // Low frequency
    }
  } catch (error) {
    console.log('Web Audio API không được hỗ trợ:', error)
  }
}

/**
 * State cho Physics Survival Runner Game
 */
export interface PhysicsSurvivalGameState {
  currentQuestionIndex: number
  score: number
  totalQuestions: number
  questions: DoorQuestQuestion[]
  currentQuestion: DoorQuestQuestion | null // Câu hỏi hiện tại đang hiển thị
  isGameActive: boolean
  isGameWon: boolean
  isGameLost: boolean
  currentLane: number // 0: trái, 1: giữa, 2: phải
  obstacles: Obstacle[] // Chướng ngại vật đang di chuyển
  speed: number // Tốc độ game (tăng dần khi đúng)
  showFeedback: boolean
  feedbackMessage: string
  feedbackType: 'correct' | 'incorrect' | null
  isJumping: boolean // Trạng thái nhảy
  jumpStartTime: number | null // Thời điểm bắt đầu nhảy
}

export interface Obstacle {
  id: string
  lane: number // 0, 1, hoặc 2
  answer: string // Đáp án hiển thị trên chướng ngại vật
  questionId: string
  correctIndex: number
  position: number // Vị trí Z (khoảng cách từ người chơi)
}

/**
 * Hook quản lý logic và state của Physics Survival Runner Game
 */
export const usePhysicsSurvivalRunner = (config: DoorQuestGameConfig) => {
  const {
    minQuestions = 6,
    maxQuestions = 10,
    lop,
    chuong,
    bai
  } = config

  const [gameState, setGameState] = useState<PhysicsSurvivalGameState>({
    currentQuestionIndex: 0,
    score: 0,
    totalQuestions: 0,
    questions: [],
    currentQuestion: null, // Câu hỏi hiện tại
    isGameActive: false,
    isGameWon: false,
    isGameLost: false,
    currentLane: 1, // Bắt đầu ở làn giữa
    obstacles: [],
    speed: 1.0, // Tốc độ ban đầu
    showFeedback: false,
    feedbackMessage: '',
    feedbackType: null,
    isJumping: false,
    jumpStartTime: null
  })

  const [isLoading, setIsLoading] = useState(false)
  const gameLoopRef = useRef<number | null>(null)
  const obstacleSpawnTimerRef = useRef<number | null>(null)
  const lastObstacleSpawnTime = useRef<number>(0)
  const isSpawningRef = useRef<boolean>(false) // Track đang spawn để tránh race condition
  const lastSpawnedQuestionIndexRef = useRef<number>(-1) // Track question index đã spawn
  const spawnQueueRef = useRef<Set<number>>(new Set()) // Track các question index đang trong queue spawn
  const gameStateRef = useRef(gameState)

  useEffect(() => {
    gameStateRef.current = gameState
  }, [gameState])

  useEffect(() => {
    ;(window as any).__debugSurvivalState = () => {
      const state = gameStateRef.current
      debugLog('State snapshot', {
        active: state.isGameActive,
        idx: state.currentQuestionIndex,
        total: state.totalQuestions,
        obstacles: state.obstacles.length,
        jumping: state.isJumping,
        speed: state.speed
      })
      return state
    }

    return () => {
      delete (window as any).__debugSurvivalState
    }
  }, [])

  /**
   * Load câu hỏi từ gameQuestions
   */
  interface LoadedQuestionsResult {
    questions: DoorQuestQuestion[]
    totalQuestions: number
  }

  const loadQuestions = useCallback(async (): Promise<LoadedQuestionsResult> => {
    if (!lop || !chuong || !bai) {
      console.error('❌ Missing lesson information')
      return { questions: [], totalQuestions: 0 }
    }

    setIsLoading(true)
    try {
      console.log('🎮 Loading questions for Physics Survival Runner...')
      console.log(`📚 Lesson: Lớp ${lop} - Chương ${chuong} - Bài ${bai}`)
      
      const allQuestions = getGameQuestions(lop, chuong, bai)
      const customConfig = getGameQuestionConfig(lop, chuong, bai)

      const effectiveMinQuestions = Math.max(
        1,
        customConfig?.minQuestions ?? minQuestions
      )
      let effectiveMaxQuestions = customConfig?.maxQuestions ?? maxQuestions
      if (typeof effectiveMaxQuestions !== 'number' || effectiveMaxQuestions < effectiveMinQuestions) {
        effectiveMaxQuestions = effectiveMinQuestions
      }

      console.log(`✅ Found ${allQuestions.length} questions`)

      if (allQuestions.length < effectiveMinQuestions) {
        throw new Error(
          `Bài học này chưa có đủ câu hỏi game.\n` +
          `Cần tối thiểu ${effectiveMinQuestions} câu, hiện có ${allQuestions.length} câu.\n` +
          `Vui lòng chơi bài học khác hoặc làm trắc nghiệm.`
        )
      }

      // Lấy tất cả câu hỏi có sẵn (không random để đảm bảo render đủ)
      // Nếu có ít hơn maxQuestions, lấy tất cả; nếu nhiều hơn, lấy maxQuestions
      const questionCount = Math.min(effectiveMaxQuestions, allQuestions.length)

      // Shuffle câu hỏi
      const shuffled = [...allQuestions].sort(() => Math.random() - 0.5)
      const selectedQuestions = shuffled.slice(0, questionCount)

      // Xáo trộn đáp án cho mỗi câu hỏi
      const questionsWithShuffledAnswers = selectedQuestions.map(q => {
        const indices = [0, 1, 2]
        const shuffledIndices = [...indices].sort(() => Math.random() - 0.5)
        const shuffledAnswers = shuffledIndices.map(i => q.answers[i])
        const newCorrectIndex = shuffledIndices.indexOf(q.correctIndex)
        
        return {
          ...q,
          answers: shuffledAnswers,
          correctIndex: newCorrectIndex
        }
      })

      console.log(`🎯 Selected ${questionsWithShuffledAnswers.length} questions`)

      return {
        questions: questionsWithShuffledAnswers,
        totalQuestions: questionsWithShuffledAnswers.length
      }
    } catch (error) {
      console.error('❌ Error loading questions:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [lop, chuong, bai, minQuestions, maxQuestions])

  /**
   * Di chuyển nhân vật sang làn khác
   */
  const moveToLane = useCallback((lane: number) => {
    if (!gameState.isGameActive || lane < 0 || lane > 2) return
    
    setGameState(prev => ({
      ...prev,
      currentLane: lane
    }))
  }, [gameState.isGameActive])

  /**
   * Nhảy để tránh va chạm với obstacle
   */
  const jump = useCallback(() => {
    if (!gameState.isGameActive || gameState.isJumping) return
    
    setGameState(prev => ({
      ...prev,
      isJumping: true,
      jumpStartTime: Date.now()
    }))
  }, [gameState.isGameActive, gameState.isJumping])

  /**
   * Tạo chướng ngại vật mới
   * Được tối ưu để tránh race condition trên Android Chrome
   */
  const spawnObstacleRef = useRef<() => void>()
  
  const spawnObstacle = useCallback(() => {
    console.log('[Spawn] spawnObstacle called')
    // Tránh race condition: không spawn nếu đang spawn hoặc đã spawn quá gần
    const now = Date.now()
    const minSpawnInterval = 2500 // Tăng lên 2.5 giây để đảm bảo trên Android
    const lastSpawnAt = lastObstacleSpawnTime.current
    // Layer 1: Kiểm tra đang spawn
    if (isSpawningRef.current) {
      console.log('[Spawn] Abort: already spawning')
      return // Đang spawn, bỏ qua
    }
    
    // Layer 2: Kiểm tra thời gian tối thiểu
    if (lastSpawnAt !== 0 && now - lastSpawnAt < minSpawnInterval) {
      console.log('[Spawn] Abort: min interval not reached', { elapsed: now - lastSpawnAt, required: minSpawnInterval })
      return // Spawn quá gần, bỏ qua
    }

    // Layer 3: Đánh dấu đang spawn NGAY LẬP TỨC (trước setState)
    isSpawningRef.current = true
    lastObstacleSpawnTime.current = now
    console.log('[Spawn] Starting spawn process')

    setGameState(prev => {
      // Layer 4: Kiểm tra lại trong setState để đảm bảo
      if (!prev.isGameActive || prev.currentQuestionIndex >= prev.totalQuestions) {
        // Reset flag nếu không spawn được
        isSpawningRef.current = false
        console.log('[Spawn] Abort in setState: inactive or finished', {
          active: prev.isGameActive,
          idx: prev.currentQuestionIndex,
          total: prev.totalQuestions
        })
        return prev
      }

      // Layer 5: Kiểm tra xem đã spawn cho question index này chưa
      // Chỉ abort nếu đã spawn cho index LỚN HƠN (không phải bằng)
      // Điều này cho phép retry nếu lần spawn trước thất bại
      if (lastSpawnedQuestionIndexRef.current > prev.currentQuestionIndex) {
        isSpawningRef.current = false
        console.log('[Spawn] Abort: question already spawned', {
          lastSpawned: lastSpawnedQuestionIndexRef.current,
          idx: prev.currentQuestionIndex
        })
        return prev // Đã spawn rồi, bỏ qua
      }
      
      // Nếu đã spawn cho index này nhưng obstacles vẫn rỗng, cho phép spawn lại
      if (lastSpawnedQuestionIndexRef.current === prev.currentQuestionIndex && prev.obstacles.length > 0) {
        isSpawningRef.current = false
        console.log('[Spawn] Abort: question already spawned with obstacles', {
          lastSpawned: lastSpawnedQuestionIndexRef.current,
          idx: prev.currentQuestionIndex,
          obstacles: prev.obstacles.length
        })
        return prev // Đã spawn rồi và có obstacles, bỏ qua
      }
      
      // Nếu đã spawn nhưng không có obstacles, cho phép spawn lại
      if (lastSpawnedQuestionIndexRef.current === prev.currentQuestionIndex && prev.obstacles.length === 0) {
        console.log('[Spawn] Retry spawn: previous spawn failed (no obstacles)', {
          lastSpawned: lastSpawnedQuestionIndexRef.current,
          idx: prev.currentQuestionIndex
        })
        // Reset ref để cho phép spawn lại
        lastSpawnedQuestionIndexRef.current = -1
        spawnQueueRef.current.delete(prev.currentQuestionIndex)
      }

      // Layer 6: Kiểm tra queue để tránh spawn trùng
      if (spawnQueueRef.current.has(prev.currentQuestionIndex)) {
        isSpawningRef.current = false
        console.log('[Spawn] Abort: question in queue', prev.currentQuestionIndex)
        return prev // Đang trong queue spawn, bỏ qua
      }

      const currentQuestion = prev.questions[prev.currentQuestionIndex]
      if (!currentQuestion) {
        isSpawningRef.current = false
        console.log('[Spawn] Abort: no question for index', prev.currentQuestionIndex)
        return prev
      }
      
      console.log('[Spawn] Creating obstacles for question', {
        idx: prev.currentQuestionIndex,
        question: currentQuestion.question
      })

      // Layer 7: Thêm vào queue TRƯỚC KHI spawn
      spawnQueueRef.current.add(prev.currentQuestionIndex)

      // Tạo 3 chướng ngại vật cho 3 làn, mỗi làn có 1 đáp án
      const newObstacles: Obstacle[] = [0, 1, 2].map(lane => ({
        id: `obstacle-${prev.currentQuestionIndex}-${lane}-${now}-${Math.random()}`,
        lane: lane,
        answer: currentQuestion.answers[lane],
        questionId: currentQuestion.id,
        correctIndex: currentQuestion.correctIndex,
        position: OBSTACLE_START_Z // Bắt đầu từ xa (Z âm), chạy dần về phía người chơi
      }))

      const newIndex = prev.currentQuestionIndex + 1
      
      // Cập nhật ref SAU KHI tạo obstacles (nhưng trước return)
      // Sử dụng newIndex để đảm bảo sync với state mới
      lastSpawnedQuestionIndexRef.current = prev.currentQuestionIndex

      // Reset spawn flag và queue sau khi state đã được cập nhật
      // Sử dụng setTimeout với delay lớn hơn để đảm bảo state đã được commit
      setTimeout(() => {
        isSpawningRef.current = false
        spawnQueueRef.current.delete(prev.currentQuestionIndex)
      }, 300) // Tăng lên 300ms để đảm bảo state đã được commit trên Android

      console.log('[Spawn] ✅ Successfully spawned', {
        oldObstacles: prev.obstacles.length,
        newObstacles: newObstacles.length,
        totalObstacles: prev.obstacles.length + newObstacles.length,
        newIndex: newIndex
      })
      
      return {
        ...prev,
        obstacles: [...prev.obstacles, ...newObstacles],
        currentQuestion: currentQuestion, // Lưu câu hỏi hiện tại
        currentQuestionIndex: newIndex
      }
    })
  }, [])

  // Lưu spawnObstacle vào ref để dùng trong effect
  useEffect(() => {
    spawnObstacleRef.current = spawnObstacle
  }, [spawnObstacle])

  /**
   * Game loop - Cập nhật vị trí chướng ngại vật
   */
  useEffect(() => {
    if (!gameState.isGameActive) {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current)
        gameLoopRef.current = null
      }
      return
    }

    const gameLoop = () => {
      setGameState(prev => {
        if (!prev.isGameActive) return prev

        // Di chuyển tất cả chướng ngại vật về phía người chơi (từ vị trí âm về 0)
        let newCurrentQuestion = prev.currentQuestion
        let newSpeed = prev.speed // Giữ tốc độ hiện tại
        let newScore = prev.score // Giữ điểm hiện tại
        let shouldShowFeedback = false
        let feedbackType: 'correct' | 'incorrect' | null = null
        let feedbackMessage = ''
        let gameOver = false
        
        const updatedObstacles = prev.obstacles.map(obstacle => ({
          ...obstacle,
          position: obstacle.position + (prev.speed * 0.1) // Tăng dần từ xa về 0
        })).filter(obstacle => {
          // Xóa chướng ngại vật đã đi qua (position > 3)
          if (obstacle.position > 3) {
            // Khi obstacle đi qua, cập nhật câu hỏi từ obstacle tiếp theo
            const nextObstacle = prev.obstacles.find(o => 
              o.id !== obstacle.id && 
              o.position < obstacle.position && 
              o.position > OBSTACLE_START_Z
            )
            if (nextObstacle && prev.questions[nextObstacle.questionId]) {
              // Tìm câu hỏi từ questionId
              const question = prev.questions.find(q => q.id === nextObstacle.questionId)
              if (question) {
                newCurrentQuestion = question
              }
            }
            return false
          }

          // Kiểm tra va chạm: nếu chướng ngại vật ở cùng làn và trong phạm vi quanh vị trí nhân vật
          // Bỏ qua collision nếu đang nhảy
          if (
            !prev.isJumping &&
            obstacle.position > PLAYER_Z_POSITION - COLLISION_BACK_OFFSET &&
            obstacle.position < PLAYER_Z_POSITION + COLLISION_FRONT_OFFSET &&
            obstacle.lane === prev.currentLane
          ) {
            // Kiểm tra đáp án: người chơi có ở làn chứa đáp án đúng không?
            const isCorrect = prev.currentLane === obstacle.correctIndex
            
            if (isCorrect) {
              // Đúng: vượt qua, tăng điểm, tăng tốc
              newScore = prev.score + 1
              newSpeed = Math.min(prev.speed + 0.3, 3.0) // Tăng tốc 0.3 mỗi câu đúng, tối đa 3.0
              shouldShowFeedback = true
              feedbackType = 'correct'
              feedbackMessage = 'Chính xác! Tăng tốc!'
              
              // Phát âm thanh khi đúng
              playSoundEffect('correct')
            } else {
              // Sai: Game over
              gameOver = true
              shouldShowFeedback = true
              feedbackType = 'incorrect'
              feedbackMessage = 'Bạn đã đâm vào tường, về nhà ôn bài kỹ hơn nhé, chúc bạn may mắn lần sau!'
              
              // Phát âm thanh khi game over
              playSoundEffect('game-over')
            }
            
            return false // Xóa chướng ngại vật
          }
          
          // Nếu đang nhảy và vượt qua obstacle, không tính điểm nhưng vẫn giữ obstacle
          // Obstacle sẽ tiếp tục di chuyển và biến mất theo logic bình thường (khi position > 3)
          // Không cần xóa obstacle ở đây, chỉ cần đảm bảo không tính điểm

          return true
        })

        // Nếu game over, dừng game
        if (gameOver) {
          return {
            ...prev,
            obstacles: updatedObstacles,
            currentQuestion: newCurrentQuestion || prev.currentQuestion,
            isGameActive: false,
            isGameLost: true,
            showFeedback: shouldShowFeedback,
            feedbackType: feedbackType,
            feedbackMessage: feedbackMessage
          }
        }

        // Kiểm tra thắng: đã vượt qua tất cả câu hỏi và không còn chướng ngại vật
        if (prev.currentQuestionIndex >= prev.totalQuestions && updatedObstacles.length === 0) {
          return {
            ...prev,
            obstacles: updatedObstacles,
            currentQuestion: newCurrentQuestion,
            score: newScore,
            speed: newSpeed,
            isGameActive: false,
            isGameWon: true
          }
        }

        // Cập nhật câu hỏi từ obstacle gần nhất (đầu tiên trong mảng)
        if (updatedObstacles.length > 0 && !newCurrentQuestion) {
          const nearestObstacle = updatedObstacles[0]
          const question = prev.questions.find(q => q.id === nearestObstacle.questionId)
          if (question) {
            newCurrentQuestion = question
          }
        }

        // Tự động kết thúc nhảy sau 0.8 giây
        let newIsJumping = prev.isJumping
        let newJumpStartTime = prev.jumpStartTime
        if (prev.isJumping && prev.jumpStartTime) {
          const jumpDuration = Date.now() - prev.jumpStartTime
          if (jumpDuration > 800) { // 0.8 giây
            newIsJumping = false
            newJumpStartTime = null
          }
        }

        // Update state với điểm và tốc độ mới
        return {
          ...prev,
          obstacles: updatedObstacles,
          currentQuestion: newCurrentQuestion || prev.currentQuestion,
          score: newScore,
          speed: newSpeed,
          showFeedback: shouldShowFeedback,
          feedbackType: feedbackType,
          feedbackMessage: feedbackMessage,
          isJumping: newIsJumping,
          jumpStartTime: newJumpStartTime
        }
      })

      gameLoopRef.current = requestAnimationFrame(gameLoop)
    }

    gameLoopRef.current = requestAnimationFrame(gameLoop)

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current)
        gameLoopRef.current = null
      }
    }
  }, [gameState.isGameActive, gameState.currentLane])

  /**
   * Spawn chướng ngại vật định kỳ
   */
  useEffect(() => {
    if (!gameState.isGameActive) {
      if (obstacleSpawnTimerRef.current) {
        clearTimeout(obstacleSpawnTimerRef.current)
        obstacleSpawnTimerRef.current = null
      }
      return
    }

    // Reset spawn state khi bắt đầu game (chỉ lần đầu khi chưa spawn gì)
    // Chỉ reset nếu chưa spawn câu nào và obstacles rỗng
    if (lastSpawnedQuestionIndexRef.current === -1 && gameState.obstacles.length === 0) {
      isSpawningRef.current = false
      lastObstacleSpawnTime.current = 0
      spawnQueueRef.current.clear()
      console.log('[Spawn] Reset spawn state (first time)')
    }

    const scheduleNextSpawn = (delay: number) => {
      if (obstacleSpawnTimerRef.current) {
        clearTimeout(obstacleSpawnTimerRef.current)
      }

      obstacleSpawnTimerRef.current = window.setTimeout(() => {
        const state = gameStateRef.current
        console.log('[Spawn] Timer fired', {
          active: state.isGameActive,
          idx: state.currentQuestionIndex,
          total: state.totalQuestions,
          obstacles: state.obstacles.length
        })
        
        if (!state.isGameActive) {
          obstacleSpawnTimerRef.current = null
          return
        }

        if (state.currentQuestionIndex >= state.totalQuestions) {
          obstacleSpawnTimerRef.current = null
          return
        }

        if (spawnObstacleRef.current) {
          console.log('[Spawn] Calling spawnObstacle')
          spawnObstacleRef.current()
        } else {
          console.log('[Spawn] ERROR: spawnObstacleRef.current is null!')
        }

        const latestState = gameStateRef.current
        console.log('[Spawn] After spawn', {
          idx: latestState.currentQuestionIndex,
          obstacles: latestState.obstacles.length
        })
        
        if (latestState.currentQuestionIndex < latestState.totalQuestions) {
          const interval = Math.max(3000, 6000 - latestState.speed * 400)
          scheduleNextSpawn(interval)
        } else {
          obstacleSpawnTimerRef.current = null
        }
      }, delay)
    }

    console.log('[Spawn] Starting spawn system')
    scheduleNextSpawn(1500)

    return () => {
      if (obstacleSpawnTimerRef.current) {
        clearTimeout(obstacleSpawnTimerRef.current)
        obstacleSpawnTimerRef.current = null
      }
    }
  }, [gameState.isGameActive])

  /**
   * Bắt đầu game
   */
  const startGame = useCallback(async () => {
    try {
      const { questions, totalQuestions } = await loadQuestions()
      if (!questions.length) {
        throw new Error('Không có câu hỏi nào cho bài học này')
      }
      
      // Reset spawn state khi bắt đầu game mới
      isSpawningRef.current = false
      lastObstacleSpawnTime.current = 0
      lastSpawnedQuestionIndexRef.current = -1
      spawnQueueRef.current.clear()
      
      setGameState(prev => ({
        ...prev,
        isGameActive: true,
        isGameWon: false,
        isGameLost: false,
        currentQuestionIndex: 0,
        score: 0,
        currentLane: 1,
        obstacles: [],
        speed: 1.0,
        currentQuestion: questions[0] ?? null,
        showFeedback: false,
        feedbackMessage: '',
        feedbackType: null,
        questions,
        totalQuestions
      }))
    } catch (error) {
      console.error('Error starting game:', error)
    }
  }, [loadQuestions])

  /**
   * Reset game
   */
  const resetGame = useCallback(() => {
    if (gameLoopRef.current) {
      cancelAnimationFrame(gameLoopRef.current)
      gameLoopRef.current = null
    }
    if (obstacleSpawnTimerRef.current) {
      clearTimeout(obstacleSpawnTimerRef.current)
      obstacleSpawnTimerRef.current = null
    }
    
    // Reset spawn state khi reset game
    isSpawningRef.current = false
    lastObstacleSpawnTime.current = 0
    lastSpawnedQuestionIndexRef.current = -1
    spawnQueueRef.current.clear()
    
    setGameState({
      currentQuestionIndex: 0,
      score: 0,
      totalQuestions: 0,
      questions: [],
      isGameActive: false,
      isGameWon: false,
      isGameLost: false,
      currentLane: 1,
      obstacles: [],
      speed: 1.0,
      currentQuestion: null,
      showFeedback: false,
      feedbackMessage: '',
      feedbackType: null,
      isJumping: false,
      jumpStartTime: null
    })
  }, [])

  return {
    gameState,
    isLoading,
    startGame,
    moveToLane,
    jump,
    resetGame,
    currentQuestion: gameState.currentQuestion || gameState.questions[gameState.currentQuestionIndex] || null
  }
}

